/**
 * The welcome after the first sign-in (0015): offers the tour with the demo
 * data once per person — the server remembers it, so it does not come back
 * on the next device. Starting and skipping both close it for good; the
 * profile and the help keep the way back in.
 *
 * Real sessions only. The demo has its own tour, which starts by itself.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { de } from '@fsv/core';
import { roleLabel } from '@fsv/core';
import { welcomePersona } from '@fsv/data';
import { Sheet } from '../components/Sheet';
import { Icon } from '../components/Icon';
import { useBackend, useSession } from '../data/hooks';
import { startTourWithDemoData } from './welcomeTour';

export function welcomeQueryKey(memberId: string) {
  return ['fsv', 'api', 'welcome', memberId] as const;
}

/** Whether the welcome is still due; `undefined` while unknown. The news wait for it. */
export function useWelcomeDue(): boolean | undefined {
  const backend = useBackend();
  const session = useSession();
  const query = useQuery({
    queryKey: welcomeQueryKey(session.me.memberId),
    queryFn: () => backend.welcomeDue(),
    enabled: session.mode === 'api' && backend.kind === 'api',
    staleTime: Infinity,
    retry: false,
  });
  if (session.mode !== 'api' || backend.kind !== 'api') return false;
  // Unreachable server: no welcome, and the news are not held back by it.
  return query.isError ? false : query.data;
}

export function WelcomeGate() {
  const backend = useBackend();
  const session = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const due = useWelcomeDue();
  const [busy, setBusy] = useState(false);
  if (!due) return null;

  const persona = welcomePersona(session.me.memberships.map((m) => m.role));
  const finish = async () => {
    setBusy(true);
    // Remembered even if the request fails: the welcome must not stand in the way.
    await backend.finishWelcome().catch(() => undefined);
    qc.setQueryData(welcomeQueryKey(session.me.memberId), false);
  };

  return (
    <Sheet
      title={de.welcome.title}
      onClose={() => void finish()}
      footer={
        <>
          <button type="button" className="btn" disabled={busy} onClick={() => void finish()}>
            {de.welcome.skip}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy}
            data-testid="welcome-start"
            onClick={async () => {
              await finish();
              const path = startTourWithDemoData(persona);
              qc.clear();
              navigate(path, { replace: true });
            }}
          >
            <Icon name="flag" />
            {de.welcome.start}
          </button>
        </>
      }
    >
      <p>{de.welcome.body}</p>
      <p className="muted">{de.welcome.role(roleLabel[persona])}</p>
      <p className="muted">{de.welcome.later}</p>
    </Sheet>
  );
}
